import { useTranslation } from 'react-i18next';
import { Minus, Plus } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { addToCart, setQuantity } from '@/Redux/Slices/guestSlice';

interface MenuItem {
  id: string;
  name: string;
  description?: string | null;
  basePrice: string | number;
  imageUrl?: string | null;
  isSoldOut?: boolean;
}

/**
 * One product in the guest menu.
 *
 * Two columns rather than one: a phone shows about three horizontal rows at a
 * time, so a 40-item menu is thirteen screens of scrolling, and the guest is
 * usually comparing options rather than reading one. The card is vertical
 * because a horizontal row does not survive the halved width -- a 64px image,
 * a name, a price and a button inside ~180px leaves the name two words wide.
 *
 * The control is a stepper once the item is in the cart, and a single add
 * button before that. The quantity on the card IS the receipt a toast cannot be
 * — it is still there once the toast has gone — and neither a second helping
 * nor an undone mistap means opening the cart.
 */
export function MenuItemCard({ item, currency }: { item: MenuItem; currency: string }) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  // Match on `itemId` alone: the menu adds lines with no notes, so a line with
  // notes came from a re-order and is a different thing to step.
  const quantity = useAppSelector(
    (s) => s.guest.cart.find((l) => l.itemId === item.id && !l.notes)?.quantity ?? 0,
  );

  const soldOut = Boolean(item.isSoldOut);

  const add = () =>
    dispatch(
      addToCart({
        itemId: item.id,
        name: item.name,
        // Money arrives as a decimal string; the cart keeps a number only to
        // total it, never to send it back.
        unitPrice: Number(item.basePrice),
        quantity: 1,
      }),
    );

  return (
    <Card className={soldOut ? 'opacity-60' : undefined}>
      <CardContent className="flex h-full flex-col gap-2 p-3">
        {item.imageUrl && (
          <img
            src={item.imageUrl}
            alt=""
            loading="lazy"
            className="aspect-square w-full rounded-lg object-cover"
          />
        )}

        <div className="min-w-0 flex-1">
          {/* Two lines, not one: Arabic product names are routinely longer than
              the column, and truncating to a single line loses the word that
              distinguishes two drinks from each other. */}
          <div className="line-clamp-2 text-sm font-medium">{item.name}</div>
          {item.description && (
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.description}</p>
          )}
        </div>

        {/* Wraps rather than overflows: in a two-column grid the card is
            ~140px wide on a 320pt phone and ~115px inside its own padding,
            while the price plus a 36/24/36 stepper needs ~150px. Without the
            wrap the stepper (which must stay `shrink-0` to keep its touch
            targets) pushed out of the card and scrolled the page sideways as
            soon as one item was in the cart. */}
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
          <span className="min-w-0 text-sm font-semibold text-accent">
            {soldOut ? t('guest.menu.soldOut') : formatCurrency(item.basePrice, currency, i18n.language)}
          </span>

          {quantity === 0 ? (
            <Button
              size="icon"
              className="h-9 w-9 shrink-0"
              aria-label={t('guest.menu.addNamed', { name: item.name })}
              disabled={soldOut}
              onClick={add}
            >
              <Plus className="h-4 w-4" />
            </Button>
          ) : (
            /* `group` rather than three separate controls: the count is not a
               button, and letting it take focus would put a dead stop between
               minus and plus for anyone using a keyboard or a switch. */
            <div className="flex shrink-0 items-center gap-1" role="group" aria-label={item.name}>
              <Button
                size="icon"
                variant="outline"
                className="h-9 w-9"
                aria-label={t('guest.menu.removeOne', { name: item.name })}
                onClick={() => dispatch(setQuantity({ itemId: item.id, quantity: quantity - 1 }))}
              >
                <Minus className="h-4 w-4" />
              </Button>
              {/* aria-live so a screen reader hears the new count after a tap;
                  the buttons keep focus, so nothing else announces it. */}
              <span className="min-w-6 text-center text-sm font-semibold tabular-nums" aria-live="polite">
                {quantity}
              </span>
              <Button
                size="icon"
                className="h-9 w-9"
                aria-label={t('guest.menu.addOne', { name: item.name })}
                disabled={soldOut}
                onClick={add}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
